import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// PRD 19 §3.2 — dev/QA path into /signup, gated by VITE_ENABLE_SIGNUP_EASTER_EGG.
// Logo behavior (matches prototype/admin/pages/login.html):
//   5 consecutive clicks → /signup?token=demo (normal flow)
//   7 consecutive clicks → /signup?expired=true (expired-link simulation)
//   600 ms debounce after the last click finalizes the count
//
// Returns:
//   { onLogoClick }: noop if the flag is disabled, so callers can wire it
//   unconditionally without an extra branch.
export function useSignupEasterEgg(): { onLogoClick: () => void } {
  const navigate = useNavigate();
  const clickCountRef = useRef(0);
  const finalizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (finalizeTimerRef.current) clearTimeout(finalizeTimerRef.current);
    };
  }, []);

  const enabled = import.meta.env.VITE_ENABLE_SIGNUP_EASTER_EGG === 'true';

  const onLogoClick = useCallback(() => {
    if (!enabled) return;
    clickCountRef.current += 1;
    if (finalizeTimerRef.current) clearTimeout(finalizeTimerRef.current);
    finalizeTimerRef.current = setTimeout(() => {
      const count = clickCountRef.current;
      clickCountRef.current = 0;
      finalizeTimerRef.current = null;
      if (count === 7) {
        navigate('/signup?expired=true');
      } else if (count >= 5) {
        navigate('/signup?token=demo');
      }
    }, 600);
  }, [enabled, navigate]);

  return { onLogoClick };
}
